import { DocumentService } from '@dealstack/frontend/shared/dataExchange/services/document/document.service';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { DocumentLinkTarget } from '@dealstack/frontend/shared/dataExchange/services/shared/types';

type RemoveDocumentArgs = {
	documentId: string;
	dealId: string;
};

type RemoveDocumentLinkArgs = RemoveDocumentArgs & DocumentLinkTarget;

export const useRemoveDocument = () => {
	const removeDocumentPersonLinkMutation = useMutation({
		mutationFn: DocumentService.removeDocumentPersonLink,
	});
	const removeDocumentTransactionLinkMutation = useMutation({
		mutationFn: DocumentService.removeDocumentTransactionLink,
	});
	const removeDocumentMutation = useMutation({
		mutationFn: DocumentService.removeDocument,
	});
	const queryClient = useQueryClient();
	const tCapManage = useTranslationCapManage();

	const removeDocument = async ({ documentId, dealId }: RemoveDocumentArgs) => {
		await removeDocumentMutation.mutateAsync(
			{
				dealId,
				documentId,
			},
			{
				onSuccess: async () => {
					showToastV2({
						title: tCapManage('documents.success.remove-document'),
						state: 'success',
					});
					await queryClient.refetchQueries({
						queryKey: [
							formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_LIBRARY_DOCUMENTS_GET, {
								dealId,
							}),
						],
					});
					await queryClient.refetchQueries({
						queryKey: [
							formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_DOCUMENTID_GET, {
								dealId,
							}),
						],
					});
				},
				onError: () => {
					showToastV2({
						title: tCapManage('documents.errors.remove-document'),
						state: 'error',
					});
				},
			}
		);
	};

	const removeDocumentLink = async ({
		documentId,
		dealId,
		...target
	}: RemoveDocumentLinkArgs) => {
		const options = {
			onSuccess: async () => {
				await queryClient.refetchQueries({
					queryKey: [
						formatUnicorn(API_PATHS.DEAL_DEALID_DOCUMENT_GET, {
							dealId,
						}),
					],
				});
				showToastV2({
					title: tCapManage('documents.success.remove-document'),
					state: 'success',
				});
			},
			onError: () => {
				showToastV2({
					title: tCapManage('documents.errors.remove-document'),
					state: 'error',
				});
			},
		};

		if (target.personId !== undefined) {
			removeDocumentPersonLinkMutation.mutate(
				{ dealId, documentId, personId: target.personId },
				options
			);
			return;
		}

		removeDocumentTransactionLinkMutation.mutate(
			{ dealId, documentId, transactionId: target.transactionId },
			options
		);
	};

	return { removeDocument, removeDocumentLink, removeDocumentMutation };
};
